/**
 * EuHistoricalViewer — single archived EU SmPC version, deep-linkable.
 *
 * The "what did version V6 say?" surface (the compare view answers "what changed
 * V5→V6"). Wraps EuHistoricalViewerEmbed (→ RegulatoryViewerEmbed) and adds:
 *   - a header with the registry version label (fetched from /meta),
 *   - a fail-closed banner when the archived version's structure is unreliable
 *     (no text layer / labelling-carton scan) — the TOC is empty in that case,
 *     so navigation falls back to the page rail, and we say so,
 *   - deep-link landing: scroll to the requested page once pages are loaded.
 *
 * Deep link: `?source=eu-history&doc=<eu_historical_documents.id>&page=<n>`
 * (parse with `parseEuHistoryDeepLink`). `doc` is the STABLE registry id — never
 * the display ordinal, which can shift when older versions are discovered.
 */
import { useEffect, useRef, useState } from 'react';
import { auth } from '../../config/firebase';
import EuHistoricalViewerEmbed, {
  type EuHistoricalViewerHandle,
} from './EuHistoricalViewerEmbed';

const BASE_URL =
  (import.meta.env['VITE_API_URL'] as string) ||
  'https://us-central1-label-doctor-f255e.cloudfunctions.net/apiProxy';

async function authHeaders(): Promise<Record<string, string>> {
  const h: Record<string, string> = {};
  const u = auth.currentUser;
  if (u) h['Authorization'] = `Bearer ${await u.getIdToken(false)}`;
  return h;
}

interface Meta {
  document_id: number;
  hc_number: string;
  drug_id: number | null;
  vstr: string;
  ordinal: number | null;
  effective_date: string | null;
  page_count: number;
  version_label: string;
  structure_reliable: boolean;
}

/** Parse `?source=eu-history&doc=<id>&page=<n>` → {docId, page} or null. */
export function parseEuHistoryDeepLink(
  search: string,
): { docId: number; page: number } | null {
  const q = new URLSearchParams(search);
  if (q.get('source') !== 'eu-history') return null;
  const docId = Number(q.get('doc'));
  if (!Number.isInteger(docId) || docId <= 0) return null;
  const page = Number(q.get('page')) || 1;
  return { docId, page };
}

/** One per-page section box: page (1-based) + normalized [x0,y0,x1,y1]. */
interface SectionSpan { page: number; box: [number, number, number, number]; }

interface Props {
  docId: number;
  /** Fallback landing page (1-based) if the section can't be resolved. */
  initialPage?: number | undefined;
  /** Normalized Y (0..1) of the section top on ``initialPage`` — scroll fallback
   *  when neither the TOC nor the geometry endpoint resolves the section. */
  initialY?: number | undefined;
  /** CEPI complete-section citation: the section number to land on (preferred
   *  via the TOC ``scrollToSection``, US-SBA parity) + box via geometry. */
  initialSection?: string | null | undefined;
  /** The auto-selected strength — passed to the section-geometry endpoint so a
   *  multi-strength version boxes the right presentation's section. */
  presentationKey?: string | null | undefined;
  onClose?: () => void;
}

export default function EuHistoricalViewer({
  docId, initialPage, initialY, initialSection, presentationKey, onClose,
}: Props) {
  const viewerRef = useRef<EuHistoricalViewerHandle>(null);
  const [meta, setMeta] = useState<Meta | null>(null);
  const [err, setErr] = useState<string | null>(null);
  // Deterministic section geometry (per-page union boxes) — drives BOTH the
  // scroll target and the region highlight. Text matching can't work: the page
  // text layer is one span per word, so a multi-word section phrase never
  // matches a single-word span. Empty until fetched (viewer still opens).
  const [sectionSpans, setSectionSpans] = useState<SectionSpan[]>([]);

  useEffect(() => {
    let live = true;
    setMeta(null);
    setErr(null);
    (async () => {
      try {
        const r = await fetch(
          `${BASE_URL}/eu-history/documents/${docId}/meta`,
          { headers: await authHeaders() },
        );
        if (!r.ok) throw new Error(`meta ${r.status}`);
        const m: Meta = await r.json();
        if (live) setMeta(m);
      } catch (e) {
        if (live) setErr(e instanceof Error ? e.message : 'failed to load');
      }
    })();
    return () => {
      live = false;
    };
  }, [docId]);

  // Fetch the cited section's geometry (deterministic; keyed by doc + section +
  // presentation). Fail-soft: on any miss we leave spans empty and the embed
  // falls back to scrollToSection(TOC) → page/Y from the citation.
  useEffect(() => {
    let live = true;
    setSectionSpans([]);
    const sec = (initialSection || '').trim();
    if (!sec) return;
    (async () => {
      try {
        const qs = new URLSearchParams({ section: sec });
        if (presentationKey) qs.set('presentation_key', presentationKey);
        const r = await fetch(
          `${BASE_URL}/eu-history/documents/${docId}/section-geometry?${qs}`,
          { headers: await authHeaders() },
        );
        if (!r.ok) return;
        const j = await r.json();
        if (live && Array.isArray(j.spans)) setSectionSpans(j.spans);
      } catch {
        /* fail-soft: no geometry → embed uses TOC/page fallback */
      }
    })();
    return () => { live = false; };
  }, [docId, initialSection, presentationKey]);

  // Deep-link landing + highlight are handled INSIDE the embed: its scroll
  // effect depends on the loaded page + TOC lists, so it fires once they mount
  // (race-safe, no timer) and prefers scrollToSection, falling back to the
  // section's first box, then the citation page/Y.

  return (
    <div className="flex flex-col h-full text-sm">
      <div className="flex items-center gap-3 px-5 py-2.5 border-b border-slate-200 bg-white shrink-0">
        <span className="font-semibold text-slate-800 truncate">
          {meta?.hc_number || '…'}
          <span className="font-normal text-slate-400"> · archived SmPC</span>
        </span>
        <span title="Beta: EU versions are still being validated against the EMA / EC sources" className="rounded-ctl border border-signal/30 px-1.5 font-mono text-[10px] tracking-[0.08em] text-signal shrink-0">BETA</span>
        {meta && (
          <span className="text-slate-500">{meta.version_label}</span>
        )}
        {onClose && (
          <button
            onClick={onClose}
            className="ml-auto rounded-md w-7 h-7 flex items-center justify-center text-slate-400 hover:bg-slate-100 hover:text-slate-700 transition-colors text-lg leading-none"
            title="Close"
          >
            ×
          </button>
        )}
      </div>

      {err && (
        <div className="px-5 py-2 text-rose-600 bg-rose-50 shrink-0">{err}</div>
      )}
      {meta && !meta.structure_reliable && (
        <div className="px-5 py-2 text-[12px] text-amber-800 bg-amber-50 border-b border-amber-200 shrink-0">
          This archived version’s section structure couldn’t be reliably
          detected (older scan or no text layer), so the contents list is
          unavailable — use the page thumbnails to navigate. The document itself
          is shown in full below.
        </div>
      )}

      <div className="flex-1 min-h-0">
        {meta && (
          <EuHistoricalViewerEmbed
            ref={viewerRef}
            docId={docId}
            landingSection={initialSection ?? undefined}
            landingPage={initialPage && initialPage >= 1 ? initialPage : undefined}
            landingY={initialY}
            sectionSpans={sectionSpans}
          />
        )}
      </div>
    </div>
  );
}
